<script lang="ts">
  // A review comment's body. Each suggestion block shows as a diff against
  // the lines its thread targets, built from the block's code text in the
  // Markdown. The prose around the blocks keeps GitHub's rendering
  // (mentions, issue links, emoji): its HTML is cut at GitHub's
  // suggested-change blocks and the pieces are paired, in order, with the
  // Markdown's. Only when there is no GitHub HTML, or its blocks do not
  // pair up, is the whole body rendered from its Markdown. A comment on a
  // span of code (logic/spanNote) shows the span compact and colored first
  // (SpanQuote), and the rest without the quote it starts with.
  import type { ReviewThread, ThreadComment } from '../api/queries';
  import type { ConversationView, ThreadPage } from '../state/rangeHost';
  import { splitSuggestions, suggestionBasis, type BodyPart } from '../logic/suggestion';
  import { splitSuggestionHtml } from '../logic/markdown';
  import { headLines } from '../state/headText';
  import Html from './Html.svelte';
  import SuggestionDiff from './SuggestionDiff.svelte';
  import SpanQuote from './SpanQuote.svelte';
  import { splitSpan, withoutSpanQuoteHtml } from '../logic/spanNote';

  interface Props {
    pr: Pick<ConversationView, 'owner' | 'repo' | 'headRefOid'>;
    /** The page's files at the head, which a suggestion is compared against. */
    page: Pick<ThreadPage, 'files' | 'filesHead'>;
    thread: Pick<ReviewThread, 'path' | 'diffSide' | 'line' | 'startLine' | 'originalLine' | 'originalStartLine'>;
    comment: Pick<ThreadComment, 'body' | 'bodyHTML' | 'diffHunk'>;
  }
  let { pr, page, thread, comment }: Props = $props();

  /** HTML and suggestions, alternating: `html` holds one more piece than `suggestions`. */
  type View =
    | { kind: 'plain' }
    | { kind: 'github'; html: string[]; suggestions: string[][] }
    | { kind: 'markdown'; parts: BodyPart[] };

  /** The span the comment is on, if it starts with one, and the body after it (its HTML too, when GitHub's can be cut). */
  const spanned = $derived(splitSpan(comment.body));
  const md = $derived(spanned.span ? spanned.text : comment.body);
  const bodyHTML = $derived(spanned.span ? withoutSpanQuoteHtml(comment.bodyHTML) : comment.bodyHTML);

  const view: View = $derived.by(() => {
    const parts = splitSuggestions(md);
    const suggestions = parts.flatMap((p) => (p.kind === 'suggestion' ? [p.lines] : []));
    if (!suggestions.length) return { kind: 'plain' };
    const html = bodyHTML?.trim() ? splitSuggestionHtml(bodyHTML) : null;
    if (html && html.length === suggestions.length + 1) return { kind: 'github', html, suggestions };
    return { kind: 'markdown', parts };
  });

  /** The file at the head, once loaded: a changed line makes the suggestion outdated. */
  let head: string[] | null = $state.raw(null);

  $effect(() => {
    if (view.kind === 'plain' || thread.diffSide !== 'RIGHT' || thread.line === null) return;
    let live = true;
    void headLines({ owner: pr.owner, repo: pr.repo, headRefOid: pr.headRefOid, files: page.files, filesHead: page.filesHead }, thread.path).then((h) => {
      if (live) head = h;
    });
    return () => (live = false);
  });

  const basis = $derived(
    suggestionBasis(
      {
        side: thread.diffSide,
        line: thread.line,
        startLine: thread.startLine,
        originalLine: thread.originalLine,
        originalStartLine: thread.originalStartLine,
        diffHunk: comment.diffHunk,
      },
      head,
    ),
  );
</script>

{#snippet diff(lines: string[])}
  <SuggestionDiff path={thread.path} original={basis.original} suggested={lines} outdated={basis.outdated} />
{/snippet}

{#if spanned.span}<SpanQuote path={thread.path} span={spanned.span} />{/if}
{#if view.kind === 'github'}
  {#each view.html as piece, i (i)}
    {#if piece.trim()}<Html html={piece} />{/if}
    {#if i < view.suggestions.length}{@render diff(view.suggestions[i])}{/if}
  {/each}
{:else if view.kind === 'markdown'}
  {#each view.parts as part, i (i)}
    {#if part.kind === 'markdown'}<Html md={part.md} />{:else}{@render diff(part.lines)}{/if}
  {/each}
{:else if md.trim() || !spanned.span}
  <Html html={bodyHTML} md={md} />
{/if}
